import React, { useState } from "react";
import { Box, ButtonBase, Stack, Typography } from "@mui/material";
import {
  BuildCircleOutlined,
  ChevronRight,
  EngineeringOutlined,
  GavelOutlined,
  LockOutlined,
  UploadFileOutlined,
  VerifiedOutlined,
} from "@mui/icons-material";
import DashCard from "../../dashboard/DashCard";
import { DASH, cardSx } from "../../dashboard/tokens";
import { usePropertyDashboard } from "../../dashboard/PropertyDashboardContext";
import type { DashboardTabId } from "../../dashboard/dashboardTabs";
import { useEntitlements } from "../../../../Hooks/useEntitlements";
import { UpgradeDialog } from "../../../../Components/PremiumGate/PremiumGate";

type Action = {
  key: string;
  label: string;
  hint: string;
  icon: React.ReactNode;
  tab: DashboardTabId;
  focus?: string;
  /** Behind the premium gate for a free owner. Finding a pro never is. */
  premium?: boolean;
};

export const QUICK_ACTIONS: Action[] = [
  {
    key: "repair",
    label: "Add a repair or improvement",
    hint: "Upload the contract or invoice",
    icon: <BuildCircleOutlined />,
    tab: "home-history",
    focus: "upload",
    premium: true,
  },
  {
    key: "document",
    label: "Upload a document",
    hint: "Deed, insurance, tax bill…",
    icon: <UploadFileOutlined />,
    tab: "documents",
    focus: "upload",
    premium: true,
  },
  {
    key: "warranty",
    label: "Add a warranty",
    hint: "Keep coverage dates handy",
    icon: <VerifiedOutlined />,
    tab: "documents",
    focus: "upload:warranty",
    premium: true,
  },
  {
    key: "permits",
    label: "Review permit history",
    hint: "Public permits on this address",
    icon: <GavelOutlined />,
    tab: "home-history",
    focus: "permits",
  },
  {
    key: "pro",
    label: "Find a trusted pro",
    hint: "Free help from trusted local pros",
    icon: <EngineeringOutlined />,
    tab: "find-a-pro",
  },
];

/**
 * Right-column shortcuts (Asher moved these ABOVE the map: "a map is fun but
 * quick actions are a higher priority"). Each jumps into the tab that does
 * the job; a premium action shows its lock to a free owner up front instead of
 * letting them click through to a wall.
 *
 * `variant="strip"`: below the wide layout the right column drops under the
 * tab, which would bury these — so the shell shows them as a row of compact
 * tiles above the tab instead (swipe sideways on phones).
 */
const QuickActions: React.FC<{ variant?: "card" | "strip" }> = ({ variant = "card" }) => {
  const { goTo, isOwner } = usePropertyDashboard();
  const { isPremium } = useEntitlements();
  const [upsell, setUpsell] = useState(false);
  const isLocked = (a: Action) => Boolean(a.premium && isOwner && !isPremium);
  const run = (a: Action) => (isLocked(a) ? setUpsell(true) : goTo(a.tab, a.focus));

  const upgrade = (
    <UpgradeDialog
      open={upsell}
      onClose={() => setUpsell(false)}
      title="Build your home's full record with Premium"
      description="Add repairs, improvements, documents and warranties, and keep them all in one place."
    />
  );

  if (variant === "strip") {
    return (
      <Box component="nav" aria-label="Quick actions" data-testid="quick-actions">
        <Typography component="h3" sx={{ fontWeight: 700, fontSize: 15, color: DASH.teal, mb: 2 }}>
          Quick Actions
        </Typography>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            overflowX: "auto",
            scrollSnapType: "x proximity",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            pb: 1, // room for the tiles' shadow
          }}
        >
          {QUICK_ACTIONS.map((a) => {
            const locked = isLocked(a);
            return (
              <ButtonBase
                key={a.key}
                onClick={() => run(a)}
                sx={{
                  ...cardSx,
                  boxShadow: "none",
                  flex: "1 0 132px",
                  maxWidth: 180,
                  scrollSnapAlign: "start",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  justifyContent: "flex-start",
                  textAlign: "left",
                  gap: 2,
                  p: 3,
                  borderRadius: "14px",
                  position: "relative",
                  transition: "background-color .15s",
                  "&:hover": { bgcolor: DASH.tealTint },
                }}
                data-testid={`quick-action-${a.key}`}
              >
                <ActionIcon action={a} size={32} />
                <Typography sx={{ fontWeight: 700, fontSize: 13.5, lineHeight: 1.3, color: "#1d2b2b" }}>{a.label}</Typography>
                {locked && (
                  <LockOutlined
                    sx={{ position: "absolute", top: 10, right: 10, fontSize: 16, color: DASH.muted }}
                    aria-label="Premium"
                  />
                )}
              </ButtonBase>
            );
          })}
        </Stack>
        {upgrade}
      </Box>
    );
  }

  return (
    <DashCard title="Quick Actions" padded={false} testId="quick-actions">
      <Stack sx={{ py: 2 }}>
        {QUICK_ACTIONS.map((a) => {
          const locked = isLocked(a);
          return (
            <ButtonBase
              key={a.key}
              onClick={() => run(a)}
              sx={{
                justifyContent: "flex-start",
                textAlign: "left",
                gap: 3,
                px: 5,
                py: 2.5,
                transition: "background-color .15s",
                "&:hover": { bgcolor: DASH.tealTint },
              }}
              data-testid={`quick-action-${a.key}`}
            >
              <ActionIcon action={a} size={36} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14.5, color: "#1d2b2b" }}>{a.label}</Typography>
                <Typography sx={{ fontSize: 12.5, color: DASH.muted }} noWrap>
                  {a.hint}
                </Typography>
              </Box>
              {locked ? (
                <LockOutlined sx={{ fontSize: 18, color: DASH.muted }} aria-label="Premium" />
              ) : (
                <ChevronRight sx={{ color: DASH.muted }} />
              )}
            </ButtonBase>
          );
        })}
      </Stack>
      {upgrade}
    </DashCard>
  );
};

const ActionIcon: React.FC<{ action: Action; size: number }> = ({ action, size }) => (
  <Box
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      borderRadius: "10px",
      bgcolor: action.key === "pro" ? DASH.amberTint : DASH.tealTint2,
      color: action.key === "pro" ? "#5b3a00" : DASH.teal,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {action.icon}
  </Box>
);

export default QuickActions;
